// Overlays the BE /me response with the flat CurrentUser shape (FE #32 / BE #29).
// Pure module — no Redux, no RTK Query — so it can be unit-tested in isolation.
//
// Until BE ships the new fields, defaults are:
//   • barber_shop.onboarded = false   (assume new shops need to onboard;
//                                       once BE backfills, real flag wins)
//   • can_services / can_hours = true for owners (derived from roles), false otherwise
//   • accent falls back to legacy `primary_color` then to brand default
//
// `any` is the right type for the input here — this is the boundary
// where the untyped BE response becomes a typed CurrentUser.
/* eslint-disable @typescript-eslint/no-explicit-any */

import {Role} from '../types'
import type {
    CurrentUser,
    CurrentUserAddress,
    CurrentUserBarberShop,
    OnboardingState,
} from '../types'

const isOwnerRole = (roles: string[] | undefined): boolean =>
    Array.isArray(roles) && roles.includes(Role.Owner)

const overlayAddress = (raw: any): CurrentUserAddress | null => {
    if (!raw || typeof raw !== 'object') return null
    return {
        id: raw.id ?? '',
        line1: raw.line1 ?? null,
        line2: raw.line2 ?? null,
        post_code: raw.post_code ?? null,
        city: raw.city ?? null,
        country: raw.country ?? null,
        latitude: typeof raw.latitude === 'number' ? raw.latitude : null,
        longitude: typeof raw.longitude === 'number' ? raw.longitude : null,
    }
}

const overlayOnboarding = (raw: any): OnboardingState => {
    const fromBe = raw?.onboarding
    if (fromBe && typeof fromBe === 'object') {
        return {
            onboarded: !!fromBe.onboarded,
            step: fromBe.step ?? 'profile',
            checklist: {
                location: !!fromBe.checklist?.location,
                team: !!fromBe.checklist?.team,
                bookio: !!fromBe.checklist?.bookio,
                share: !!fromBe.checklist?.share,
            },
        }
    }
    return {
        onboarded: !!raw?.barber_shop?.onboarded,
        step: 'profile',
        checklist: {location: false, team: false, bookio: false, share: false},
    }
}

const overlayBarberShop = (raw: any): CurrentUserBarberShop => {
    const shop = raw?.barber_shop ?? {}
    return {
        id: shop.id ?? '',
        name: shop.name ?? '',
        legal_name: shop.legal_name,
        slug: shop.slug ?? '',
        address: overlayAddress(shop.address),
        phone: shop.phone,
        public_email: shop.public_email,
        public_number: shop.public_number,
        about: shop.about,
        logo: shop.logo ?? null,
        accent: shop.accent ?? shop.primary_color ?? '#0088FF',
        onboarded: shop.onboarded ?? false,
        account_type: shop.account_type,
        language: shop.language ?? 'en',
        hidden_from_explore: shop.hidden_from_explore,
    }
}

export const overlayCurrentUser = (raw: any): CurrentUser => {
    const owner = isOwnerRole(raw?.roles)
    return {
        id: raw?.id ?? '',
        email: raw?.email ?? '',
        first_name: raw?.first_name,
        last_name: raw?.last_name,
        phone_number: raw?.phone_number,
        photo: raw?.photo ?? null,
        roles: Array.isArray(raw?.roles) ? raw.roles : [],
        // Owners always can; non-owners default to false until BE ships
        // the per-barber grant (FE #30 / BE #27). Real flags from BE win.
        can_services: raw?.can_services ?? owner,
        can_hours: raw?.can_hours ?? owner,
        active: raw?.active ?? true,
        position: raw?.position,
        barber_shop: overlayBarberShop(raw),
        onboarding: overlayOnboarding(raw),
    }
}
